import { appLogger } from "@/utils/AppLogger";
import { useCallback } from "react";

export enum PermissionStatusEnum {
  DENIED = "denied",
  GRANTED = "granted",
  PROMPT = "prompt",
}

export type PermissionStatus = PermissionStatusEnum;

export interface UseMicPermissionReturnType {
  askMicPermission: () => Promise<PermissionStatusEnum.DENIED | PermissionStatusEnum.GRANTED>;
  verifyMicPermission: () => Promise<PermissionStatus | null>;
}

export const useMicPermission = () => {
  const verifyMicPermission = useCallback(async () => {
    try {
      const status = await navigator.permissions.query({ name: "microphone" as PermissionName });

      if (status.state === "granted") {
        appLogger(`[MICROPHONE] Permission is granted`, { type: "debug" });
        return status.state;
      }

      if (status.state === "denied") {
        appLogger(`[MICROPHONE] Permission is denied`, { type: "error" });
        return status.state;
      }

      appLogger(`[MICROPHONE] Permission is in prompt state`, { type: "debug" });
      return status.state;
    } catch (error) {
      appLogger(`[MICROPHONE] Error on microphone permission - ${error}`, { type: "error" });
      return null;
    }
  }, []);

  const askMicPermission = useCallback(async () => {
    try {
      const permission = await verifyMicPermission();

      if (permission === "denied") {
        return "denied";
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      appLogger(`[MICROPHONE] Permission request was granted`, { type: "info" });
      return "granted";
    } catch (error) {
      appLogger(`[MICROPHONE] Permission request was denied ${error}`, { type: "error" });
      return "denied";
    }
  }, [verifyMicPermission]);

  return { askMicPermission, verifyMicPermission };
};
